import { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { BlockProps, BlockVariant, BlockVariantSide } from "@store/Components/Common/Block";
import separated from "@store/Components/Common/Block/variant/types/separated";
import { ShareEntityButtonStyles } from "@store/Overrides/Components/ShareEntityButton";
import {
    checkoutOrderTotalItemStyles,
    checkoutStickyWrapperStyles,
    clearfixStyles,
    editLinkStyles,
    gridGapStyles,
    scalableIconStyles,
    sideBoxStyles,
} from "@store/theme/common/mixins";
import { baseGridProps, StoreBaseGridProps } from "@store/theme/common/props/baseGrid";
import { StoreGridProps } from "@store/theme/common/props/grid";
import { COMMON, TYPOGRAPHY } from "@store/theme/design";
import breakpointMediaQueries, { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import { css } from "styled-components";

// -- GRID

export interface StoreCheckoutGridProps extends StoreBaseGridProps {
    sideColumnMobile?: GridItemProps;
    sideColumnDesktop?: GridItemProps;
}

const sideColumnProps: GridItemProps = {
    width: [12, 12, 5, 4, 4],
    printWidth: 12,
};

export const checkoutGridProps: StoreCheckoutGridProps = {
    container: baseGridProps.container,
    mainColumn: {
        width: [12, 12, 7, 8, 8],
        printWidth: 12,
    },
    sideColumn: sideColumnProps,
    sideColumnMobile: {
        ...sideColumnProps,
        css: css`
            ${sideColumnProps?.css}
            display: block;
            ${props =>
                breakpointMediaQueries(
                    props.theme,
                    [
                        null,
                        null,
                        css`
                            display: none;
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    sideColumnDesktop: {
        ...sideColumnProps,
        css: css`
            ${sideColumnProps?.css}
            display: none;
            ${props =>
                breakpointMediaQueries(
                    props.theme,
                    [
                        null,
                        null,
                        css`
                            display: block;
                            padding-top: 0px;
                            &>div {
                                padding-top: 15px;
                                position: sticky;
                                top: 60px;
                            }
                        `,
                    ],
                    mediaOptions.min,
                )}

            @media print {
                display: none !important;
            }
        `,
    },
};

// -- BLOCK

export const checkoutBlockProps: BlockProps = {
    preserveSpacing: true,
    margin: COMMON.spacing.narrowContent,
    variant: BlockVariant.Separated,
    variantSide: BlockVariantSide.Top,
    variantProps: {
        separated: {
            ...separated.defaultProps,
            adaptToContext: true,
            border: {
                ...separated.defaultProps.border,
                width: "3px",
            },
        },
    },
    /* Poolcorp Custom Code */
    wrapperCss: css`
        position: relative;
    `,
    /* Poolcorp Custom Code */
};

// -- TITLES

export interface StoreCheckoutTitleProps {
    wrapper?: InjectableCss;
    heading?: TypographyPresentationProps;
    buttonsWrapper?: InjectableCss;
}

export const checkoutTitleProps: StoreCheckoutTitleProps = {
    wrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            border-bottom: 3px solid ${getColor("common.border")};
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                        { values: COMMON.spacing.narrowContent, properties: ["margin-bottom"] },
                        { values: [1, null, 1.25, null, 1.5], properties: ["padding-bottom"], unit: "em" },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    heading: {
        variant: "h1",
        forwardAs: "h1",
        css: css`
            margin-bottom: 0;
        `,
    },
    buttonsWrapper: {
        css: css`
            display: flex;
            margin-left: auto;
            ${checkoutStickyWrapperStyles()}
        `,
    },
};

export interface StoreCheckoutTitleWithLinksProps {
    heading?: TypographyPresentationProps;
    container?: InjectableCss;
    wrapper?: InjectableCss;
    wrapperItem?: InjectableCss;
    item?: InjectableCss;
    printLink?: LinkPresentationProps;
    backLink?: LinkPresentationProps;
    shareLink?: ShareEntityButtonStyles;
    downloadButton?: LinkPresentationProps;
}

export const getCheckoutTitleLinkProps = (iconSrc?: string) => {
    const props: LinkPresentationProps = {};

    if (iconSrc) {
        props.icon = {
            iconProps: {
                src: iconSrc,
                css: css`
                    ${scalableIconStyles()}
                    font-size: 1.5em;
                    margin-right: 0.3em;
                    transition: inherit !important;
                `,
            },
        };
    }

    props.css = css`
        ${editLinkStyles()}
        display: block;
    `;

    return props;
};

export const checkoutTitleWithLinksProps: StoreCheckoutTitleWithLinksProps = {
    heading: checkoutTitleProps.heading,
    container: {
        css: css`
            ${checkoutTitleProps.wrapper?.css}
            ${clearfixStyles()}
            display: block;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            flex-flow: row wrap;
            margin: 0.5em 0 0 -1.5em;
        `,
    },
    wrapperItem: {
        css: css`
            display: flex;
            margin: 0.5em 0 0 1.5em;
        `,
    },
    item: {
        css: css`
            display: flex;
            align-items: center;

            &::before {
                content: "";
                position: relative;
                top: 0.1em;
                display: block;
                width: 1px;
                height: 1em;
                margin: 0 0.5em;
                background-color: ${getColor("common.border")};
            }

            &:first-child::before {
                display: none;
            }
        `,
    },
    backLink: getCheckoutTitleLinkProps("ArrowLeft"),
    printLink: getCheckoutTitleLinkProps("Printer"),
    shareLink: {
        link: getCheckoutTitleLinkProps("Mail"),
    },
    downloadButton: getCheckoutTitleLinkProps("Download"),
};

export const checkoutSubtitleProps: TypographyPresentationProps = {
    variant: "h5",
    forwardAs: "h2",
    css: css`
        margin-bottom: ${TYPOGRAPHY.spacing.base};
    `,
};

export interface StoreCheckoutInnerGridProps extends StoreGridProps {}

export const checkoutAddressGridProps: StoreCheckoutInnerGridProps = {
    container: {
        gap: 0,
        css: css`
            ${gridGapStyles(["30px"], [TYPOGRAPHY.spacing.base])}
        `,
    },
    item: {
        width: [12, 6, 6, 5, 5],
        css: css`
            flex-direction: column;
        `,
    },
};

export interface StoreCheckoutInfoGridProps extends StoreCheckoutInnerGridProps {
    largeItem?: GridItemProps;
    largeSubItem?: GridItemProps;
}

export const checkoutInfoGridProps: StoreCheckoutInfoGridProps = {
    container: {
        gap: 0,
        css: css`
            ${gridGapStyles(["30px"], ["0.5em"])}
            font-size: ${TYPOGRAPHY.size.small};

            ${GridOffset} {
                @media print {
                    display: flex !important;
                }
            }
        `,
    },
    item: {
        width: [12, 4, 6, 4, 4],
        printWidth: 4,
        css: css`
            flex-direction: column;
        `,
    },
    largeItem: {
        width: [12, 8, 6, 8, 8],
        printWidth: 8,
        css: css`
            flex-direction: column;
        `,
    },
    largeSubItem: {
        width: [12, 6, 12, 6, 6],
        printWidth: 6,
        css: css`
            flex-direction: column;
        `,
    },
};

export interface StoreCheckoutInfoProps {
    label?: TypographyPresentationProps | TypographyProps;
    value?: TypographyPresentationProps | TypographyProps;
}

export const checkoutInfoProps: StoreCheckoutInfoProps = {
    label: {
        variant: null as any,
        as: "h3",
        forwardAs: "h3",
        css: css`
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                        {
                            values: [TYPOGRAPHY.weight.bold, null, null, null, TYPOGRAPHY.weight.black],
                            properties: ["font-weight"],
                        },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    value: {
        variant: null as any,
        as: "span",
        forwardAs: "span",
    },
};

// ORDER TOTAL

export interface StoreOrderTotalStyles {
    container?: GridContainerProps;
    labelGridItem?: GridItemProps;
    valueGridItem?: GridItemProps;
    title?: TypographyProps;
    titleGridItem?: GridItemProps;
    value?: TypographyProps;
    totalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    subtitle?: TypographyProps;
    subTotalcontainer?: GridContainerProps;
    subTotalPerBranchGridItemFirst?: GridItemProps;
    subTotalPerBranchGridItem?: GridItemProps;
    titleSubTotalPerBranchGridItem?: GridItemProps;
    subTotalBranchGridItem?: GridItemProps;
    labelSubTotalPerBranch?: InjectableCss;
    valueSubTotalPerBranch?: InjectableCss;
}

const orderTotalValueProps = {
    css: css`
        margin-left: auto;
    `,
};

export const orderTotalProps: StoreOrderTotalStyles = {
    container: {
        gap: 0,
        css: css`
            background-color: ${getColor("common.background")};
            ${sideBoxStyles()}
        `,
    },
    title: {
        size: TYPOGRAPHY.size.base,
    },
    titleGridItem: {
        width: 12,
        css: css`
            padding: 0 0 1em;
            &:last-child {
                padding-bottom: 0;
            }
            &:not(:first-child) {
                margin-top: 1.25em !important;
            }
            font-weight: ${TYPOGRAPHY.weight.black};
        `,
    },
    labelGridItem: {
        width: 6,
        css: css`
            ${checkoutOrderTotalItemStyles(2)}
            flex-direction: column;
        `,
    },
    valueGridItem: {
        width: 6,
        css: css`
            ${checkoutOrderTotalItemStyles(1)}
            align-items: flex-end;
        `,
    },
    value: orderTotalValueProps,
    totalLabel: {
        weight: TYPOGRAPHY.weight.bold,
    },
    totalValue: {
        ...orderTotalValueProps,
        weight: TYPOGRAPHY.weight.bold,
    },
    subtitle: {
        italic: true,
        size: TYPOGRAPHY.size.tiny,
        color: "text.accent",
        lineHeight: TYPOGRAPHY.lineHeight.smaller,
        css: css`
            margin-top: 0.25em;
        `,
    },
    subTotalPerBranchGridItemFirst: {
        width: 12,
        css: css`
            padding: 10px 0;
        `,
    },
    subTotalPerBranchGridItem: {
        width: 12,
        css: css`
            padding: 10px 0;
            border-top: 1px solid ${getColor("common.border")};
        `,
    },
    subTotalcontainer: {
        gap: 0,
        css: css`
            font-size: ${TYPOGRAPHY.size.smallest};
        `,
    },
    titleSubTotalPerBranchGridItem: {
        width: 12,
        css: css`
            padding: 0 0 0.5em;
        `,
    },
    subTotalBranchGridItem: {
        width: 12,
        css: css`
            padding: 0 0 0.25em 1em;
            flex-direction: row;
            justify-content: space-between;
            &:last-child {
                padding-bottom: 0;
            }
        `,
    },
    labelSubTotalPerBranch: {
        css: css`
            padding: 0;
            flex-grow: 1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        `,
    },
    valueSubTotalPerBranch: {
        css: css`
            padding: 0 0 0 1em;
            align-items: flex-end;
            flex-grow: 0;
            lex-shrink: 0;
        `,
    },
};
